import { ConnectionType } from '../interfaces/enums.interface';

/**
 * Display metadata for one connection type: the SVG icon (registered in
 * {@link IconRegistryService}), a Material font-icon fallback, a human label,
 * and a short description.
 */
export interface ConnectionTypeMetadata {
  /** Registered `svgIcon` name for `<mat-icon [svgIcon]>`. */
  svgIcon: string;
  /** Material font-icon ligature for `<mat-icon>`, used where no SVG is shown. */
  matIcon: string;
  /** Human-readable label (e.g. "Object Storage"). */
  label: string;
  /** One-line description of what the connection type is for. */
  description: string;
}

/**
 * The canonical lowercase keys used across the connection UI. These match
 * `ConnectionDisplay['type']` and `SupportedConnectionType`, and are produced
 * by lower-casing the {@link ConnectionType} enum values.
 */
export type ConnectionMetadataKey =
  | 'database'
  | 'datafusion'
  | 'llm'
  | 'box'
  | 'objectstorage'
  | 'service'
  | 'unknown';

/**
 * Single source of truth for connection-type icons, labels, and descriptions.
 *
 * Previously this mapping was duplicated across `connections.component.ts`
 * (`getConnectionIcon`/`getConnectionSvgIcon`) and hard-coded in the
 * `add-connection-dialog` type selector. Consumers should read from here so the
 * icon and label for a given type stay consistent everywhere.
 */
export const CONNECTION_METADATA: Record<ConnectionMetadataKey, ConnectionTypeMetadata> = {
  database: {
    svgIcon: 'connection-database',
    matIcon: 'storage',
    label: 'Database',
    description: 'Connect to a PostgreSQL or SQL Server database.',
  },
  datafusion: {
    svgIcon: 'connection-datafusion',
    matIcon: 'data_object',
    label: 'DataFusion',
    description: 'Query a DataFusion compute engine.',
  },
  llm: {
    // No dedicated SVG exists for LLM; the generic cloud icon is the fallback,
    // matching connections.component's getConnectionSvgIcon().
    svgIcon: 'connection-cloud',
    matIcon: 'smart_toy',
    label: 'LLM',
    description: 'Connect to a large language model provider.',
  },
  box: {
    svgIcon: 'connection-box',
    matIcon: 'folder',
    label: 'Box',
    description: 'Connect to Box.com cloud file storage.',
  },
  objectstorage: {
    svgIcon: 'connection-object-storage',
    matIcon: 'inventory_2',
    label: 'Object Storage',
    description: 'Connect to S3, GCS, or Azure Blob object storage.',
  },
  service: {
    // No dedicated SVG for Service; cloud is the fallback. The type selector
    // shows the miscellaneous_services font-icon.
    svgIcon: 'connection-cloud',
    matIcon: 'miscellaneous_services',
    label: 'Service',
    description: 'Connect to a global or custom external HTTP service.',
  },
  unknown: {
    svgIcon: 'connection-cloud',
    matIcon: 'cloud',
    label: 'Connection',
    description: 'External connection.',
  },
};

/**
 * Normalizes a {@link ConnectionType} enum value or any string into a
 * {@link ConnectionMetadataKey}. Unrecognized values map to `'unknown'`.
 */
export function normalizeConnectionMetadataKey(type: ConnectionType | string): ConnectionMetadataKey {
  switch (String(type).toLowerCase()) {
    case 'database':
      return 'database';
    case 'datafusion':
      return 'datafusion';
    case 'llm':
      return 'llm';
    case 'box':
      return 'box';
    case 'objectstorage':
      return 'objectstorage';
    case 'service':
      return 'service';
    default:
      return 'unknown';
  }
}

/**
 * Returns the display metadata for a connection type. Accepts the
 * {@link ConnectionType} enum, its lowercase form, or any string; unknown
 * inputs return the `'unknown'` entry rather than throwing.
 */
export function getConnectionMetadata(type: ConnectionType | string): ConnectionTypeMetadata {
  return CONNECTION_METADATA[normalizeConnectionMetadataKey(type)];
}
